import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const PdfTemplateVariableSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(["string", "number", "boolean", "array", "object"]).optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  sampleValue: z.unknown().optional(),
});

export const PdfPageSetupSchema = z.object({
  size: z.enum(["A4", "A3", "A5", "LETTER", "LEGAL", "TABLOID"]).optional(),
  orientation: z.enum(["portrait", "landscape"]).optional(),
  marginTop: z.number().optional(),
  marginBottom: z.number().optional(),
  marginLeft: z.number().optional(),
  marginRight: z.number().optional(),
});

export const PdfTemplateVersionStatusSchema = z.enum(["draft", "finalized"]);

export const PdfTemplateSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  jsonSpec: z.record(z.string(), z.unknown()),
  variables: z.array(PdfTemplateVariableSchema),
  pageSetup: PdfPageSetupSchema.nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  pinnedVersionId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const PdfTemplateVersionSchema = z.object({
  id: UUIDSchema,
  pdfTemplateId: UUIDSchema,
  parentVersionId: UUIDSchema.nullable(),
  status: PdfTemplateVersionStatusSchema,
  version: z.number().int(),
  changelog: z.string().nullable(),
  createdBy: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()),
  variables: z.array(PdfTemplateVariableSchema).nullable(),
  pageSetup: PdfPageSetupSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListPdfTemplatesInputSchema = z.object({});
export const ListPdfTemplatesResponseSchema = z.object({
  templates: z.array(PdfTemplateSchema),
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const GetPdfTemplateInputSchema = z.object({ id: UUIDSchema });
export const GetPdfTemplateResponseSchema = z.object({
  template: PdfTemplateSchema,
});

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const CreatePdfTemplateInputSchema = z.object({
  name: z.string().min(1),
  jsonSpec: z.record(z.string(), z.unknown()),
  variables: z.array(PdfTemplateVariableSchema),
  pageSetup: PdfPageSetupSchema.nullable().optional(),
  isDefault: z.boolean().optional(),
});
export const CreatePdfTemplateResponseSchema = z.object({
  template: PdfTemplateSchema,
});

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const UpdatePdfTemplateInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  variables: z.array(PdfTemplateVariableSchema),
  pageSetup: PdfPageSetupSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});
export const UpdatePdfTemplateResponseSchema = z.object({
  template: PdfTemplateSchema,
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const DeletePdfTemplateInputSchema = z.object({ id: UUIDSchema });
export const DeletePdfTemplateResponseSchema = z.object({
  success: z.boolean(),
});

// ---------------------------------------------------------------------------
// Render Preview (returns a base64-encoded PDF)
// ---------------------------------------------------------------------------

export const RenderPdfPreviewInputSchema = z.object({
  jsonSpec: z.record(z.string(), z.unknown()),
  variables: z.record(z.string(), z.unknown()),
});
export const RenderPdfPreviewResponseSchema = z.object({
  pdfBase64: z.string(),
  metadata: z.object({
    resolvedBlocks: z.array(
      z.object({
        blockId: UUIDSchema,
        versionId: UUIDSchema,
        version: z.number().int(),
      }),
    ),
  }),
});

// ---------------------------------------------------------------------------
// List Versions
// ---------------------------------------------------------------------------

export const ListPdfTemplateVersionsInputSchema = z.object({
  pdfTemplateId: UUIDSchema,
});
export const ListPdfTemplateVersionsResponseSchema = z.object({
  versions: z.array(PdfTemplateVersionSchema),
});

// ---------------------------------------------------------------------------
// Create Draft
// ---------------------------------------------------------------------------

export const CreatePdfTemplateDraftInputSchema = z.object({
  pdfTemplateId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  variables: z.array(PdfTemplateVariableSchema).optional(),
  pageSetup: PdfPageSetupSchema.nullable().optional(),
});
export const CreatePdfTemplateDraftResponseSchema = z.object({
  version: PdfTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Update Draft
// ---------------------------------------------------------------------------

export const UpdatePdfTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  variables: z.array(PdfTemplateVariableSchema).optional(),
  pageSetup: PdfPageSetupSchema.nullable().optional(),
});
export const UpdatePdfTemplateDraftResponseSchema = z.object({
  version: PdfTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Finalize Draft
// ---------------------------------------------------------------------------

export const FinalizePdfTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
});
export const FinalizePdfTemplateDraftResponseSchema = z.object({
  version: PdfTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Publish Version
// ---------------------------------------------------------------------------

export const PublishPdfTemplateVersionInputSchema = z.object({
  pdfTemplateId: UUIDSchema,
  versionId: UUIDSchema,
});
export const PublishPdfTemplateVersionResponseSchema = z.object({
  template: PdfTemplateSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const pdfTemplatesContracts = {
  list: oc
    .input(ListPdfTemplatesInputSchema)
    .output(ListPdfTemplatesResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetPdfTemplateInputSchema)
    .output(GetPdfTemplateResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreatePdfTemplateInputSchema)
    .output(CreatePdfTemplateResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdatePdfTemplateInputSchema)
    .output(UpdatePdfTemplateResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeletePdfTemplateInputSchema)
    .output(DeletePdfTemplateResponseSchema)
    .route({ method: "POST" }),
  renderPreview: oc
    .input(RenderPdfPreviewInputSchema)
    .output(RenderPdfPreviewResponseSchema)
    .route({ method: "POST" }),
  listVersions: oc
    .input(ListPdfTemplateVersionsInputSchema)
    .output(ListPdfTemplateVersionsResponseSchema)
    .route({ method: "GET" }),
  createDraft: oc
    .input(CreatePdfTemplateDraftInputSchema)
    .output(CreatePdfTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  updateDraft: oc
    .input(UpdatePdfTemplateDraftInputSchema)
    .output(UpdatePdfTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  finalizeDraft: oc
    .input(FinalizePdfTemplateDraftInputSchema)
    .output(FinalizePdfTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  publishVersion: oc
    .input(PublishPdfTemplateVersionInputSchema)
    .output(PublishPdfTemplateVersionResponseSchema)
    .route({ method: "POST" }),
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PdfTemplateResponse = z.infer<typeof PdfTemplateSchema>;
export type PdfTemplateVariableResponse = z.infer<
  typeof PdfTemplateVariableSchema
>;
export type PdfTemplateVersionResponse = z.infer<
  typeof PdfTemplateVersionSchema
>;
export type PdfPageSetupResponse = z.infer<typeof PdfPageSetupSchema>;
